import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Crown, Sparkles, TrendingUp, Video, Zap } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";

// Mirrors backend/core.py SUBSCRIPTION_TIERS. quarter_credits =
// credits_per_month * 3 (quarterly billing window).
const TIER_DETAILS = {
  beginner: { label: "BEGINNER", credits: 75, color: "cyan", icon: Sparkles,
    nextStep: "Record your first video and start building your audience." },
  experimenter: { label: "EXPERIMENTER", credits: 180, color: "violet", icon: TrendingUp,
    nextStep: "Customize your channel and reach a wider audience." },
  producer: { label: "PRODUCER", credits: 450, color: "amber", icon: Crown,
    nextStep: "Build your broadcasting network and start earning commissions." },
};

const PALETTE = {
  cyan:   { bg: "bg-cyan-500/15", border: "border-cyan-500", text: "text-cyan-300", btn: "bg-cyan-600 hover:bg-cyan-500" },
  violet: { bg: "bg-violet-500/15", border: "border-violet-500", text: "text-violet-300", btn: "bg-violet-600 hover:bg-violet-500" },
  amber:  { bg: "bg-amber-500/15", border: "border-amber-500", text: "text-amber-300", btn: "bg-amber-600 hover:bg-amber-500" },
};

/**
 * SubscriptionSuccessModal — Listens for a `subscription-success`
 * window event (dispatched by usePaymentRedirectHandler) and opens a
 * persistent celebration modal that the user must explicitly dismiss.
 *
 * Why a modal not a toast: a toast vanishes in 2-4s. New patrons
 * deserve a moment to actually understand what just happened, what
 * they got, and what to do next.
 *
 * Reads the user's tier from auth context (refreshed before the event
 * fires). Falls back to "beginner" labelling if the tier hasn't
 * propagated yet — the modal stays useful regardless.
 */
const SubscriptionSuccessModal = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onSuccess = () => setOpen(true);
    window.addEventListener("subscription-success", onSuccess);
    return () => window.removeEventListener("subscription-success", onSuccess);
  }, []);

  const tierKey = (user?.subscription_tier || "beginner").toLowerCase();
  const details = TIER_DETAILS[tierKey] || TIER_DETAILS.beginner;
  const palette = PALETTE[details.color] || PALETTE.cyan;
  const Icon = details.icon;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="bg-gray-900 border-gray-800 text-white max-w-md"
        data-testid="subscription-success-modal"
      >
        <DialogHeader>
          <div className={`mx-auto mb-3 w-16 h-16 rounded-full ${palette.bg} ${palette.border} border-2 flex items-center justify-center`}>
            <CheckCircle2 className={`h-9 w-9 ${palette.text}`} />
          </div>
          <DialogTitle className="text-center text-2xl">
            Welcome aboard{user ? `, @${user.username}` : ""}!
          </DialogTitle>
          <DialogDescription className="text-center text-gray-300 mt-2 text-base">
            You're now a <span className={`font-bold ${palette.text}`}>{details.label}</span> patron of GlobalEyes.TV.
          </DialogDescription>
        </DialogHeader>

        <div className={`mt-2 p-4 rounded-lg ${palette.bg} border ${palette.border}`}>
          <div className="flex items-center gap-2 mb-2">
            <Icon className={`h-5 w-5 ${palette.text}`} />
            <span className="font-semibold text-white">What you just got</span>
          </div>
          <ul className="space-y-1.5 text-sm text-gray-200">
            <li>· <strong>+{details.credits} credits</strong> for the quarter (billed quarterly, renews automatically)</li>
            <li>· One of the <strong>100 founding-patron spots</strong> claimed</li>
            <li>· Full recording + upload access on your channel</li>
          </ul>
        </div>

        <div className="mt-3 text-sm text-gray-400 leading-snug">
          <strong className="text-white">Next:</strong> {details.nextStep}
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <Button
            onClick={() => {
              setOpen(false);
              navigate("/dashboard?tab=videos");
              window.dispatchEvent(new CustomEvent("open-recorder"));
            }}
            data-testid="success-record-cta"
            className={`${palette.btn} text-white font-semibold`}
          >
            <Video className="h-4 w-4 mr-2" /> Record my first video
          </Button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="success-dismiss"
            className="text-gray-500 hover:text-gray-300 text-xs py-1 transition-colors"
          >
            I'll explore the dashboard first
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionSuccessModal;
